import { useCallback, useRef } from 'react';
import type { JSX } from 'react';
import { Eye, Play } from 'lucide-react';
import { useSessionContext } from '../contexts/SessionContext';
import { useI18n } from '../i18n';
import ShowMapConfirm from './ShowMapConfirm';
import { useStudioCommands } from './studio/useStudioCommands';
import { useShowMapToPlayers } from './studio/useShowMapToPlayers';
import { usePartyCount } from '../hooks/usePartyCount';
import { Button } from './ui/button';
import { Crumbs, type Crumb } from './ui/crumbs';
import { pagePath } from '../lib/page-tree';

export interface MapViewStatusProps {
  /** The map this surface shows the GM (table: viewingPageId; Studio: the pane's page). */
  readonly viewingPageId: string;
  /** Local only: put the players' map on this surface. Never activates anything. */
  readonly onReturnToPlayers: () => void;
  /** 'return' — the table goes back; 'open' — Studio opens the players' map as a tab. */
  readonly returnAction?: 'return' | 'open';
  /** The host draws the strip's frame (a Studio map header shares one line with its menu). */
  readonly embedded?: boolean;
  /**
   * Sidebar polish П9: the GM's map is named by its path «Мир › Регион › Карта»; a level opens that map for this GM
   * alone (the players stay). Absent — the path is words only.
   */
  readonly onOpenLevel?: (pageId: string) => void;
}

/**
 * Which map the GM sees and which the players see — a thin strip right above
 * the working map, whatever sidebar section or fullscreen (GM Studio 5.1).
 * In the flow, not over the canvas: the canvas edges carry the GM's own
 * controls (map-expand arrows, tool flyouts, windows). The names give way
 * first — they truncate, the full text is in their title — and the actions
 * never leave the strip: in a column too narrow even for them they wrap to a
 * second line instead of being clipped. «Players see»
 * is the confirmed activePageId (moved only by the page:activated echo), so
 * nothing here claims a success the server has not confirmed.
 *
 * Two separate actions: going back to the players' map changes only this
 * GM's own view; showing a map to the players is the existing page:activate,
 * sent only after a confirmation that names the map.
 */
/**
 * The «show to players» flow of a map strip (the table's strip and the Studio's one line): the confirmed players' map,
 * the GM's own attempt and the confirmation that names the map. One place for both strips.
 */
export function useMapStripShow(): {
  readonly nameOf: (pageId: string) => string | undefined;
  readonly flow: ReturnType<typeof useShowMapToPlayers>;
} {
  const { mp, pages } = useSessionContext();
  const { t } = useI18n();
  // Refusals are shown here; the surface's own router reports command:error too.
  const commands = useStudioCommands(mp, t, { quiet: true });
  const nameOf = useCallback(
    (pageId: string): string | undefined => pages.pages.find((p) => p.id === pageId)?.name,
    [pages.pages],
  );
  const pagesRef = useRef(pages.pages);
  pagesRef.current = pages.pages;
  const activePageIdRef = useRef(pages.activePageId);
  activePageIdRef.current = pages.activePageId;
  const partyCount = usePartyCount();
  const flow = useShowMapToPlayers({
    partyCount,
    create: commands.create,
    t,
    mp,
    pageExists: (pageId: string): boolean => pagesRef.current.some((p) => p.id === pageId),
    activePageId: () => activePageIdRef.current,
    nameOf: (pageId: string) => pagesRef.current.find((p) => p.id === pageId)?.name,
  });
  return { nameOf, flow };
}

/** The confirmation of a strip's «Show to players…»: names the map and what the players see now. */
export function StripShowConfirm({ flow, nameOf }: { readonly flow: ReturnType<typeof useShowMapToPlayers>; readonly nameOf: (pageId: string) => string | undefined }): JSX.Element {
  const { pages } = useSessionContext();
  const { t } = useI18n();
  const { confirmId, cancel, confirm, carry } = flow;
  const activeName = nameOf(pages.activePageId) ?? '…';
  return (
    <ShowMapConfirm
      open={confirmId !== undefined}
      onOpenChange={(open) => {
        if (!open) cancel();
      }}
      title={t('mapView.confirmTitle').replace('{name}', confirmId !== undefined ? (nameOf(confirmId) ?? '') : '')}
      description={t('mapView.confirmBody')
        .replace('{name}', confirmId !== undefined ? (nameOf(confirmId) ?? '') : '')
        .replace('{current}', activeName)}
      confirmLabel={t('mapView.confirm')}
      // Deleted while the dialog was open, or already shown (another GM): the hook sends nothing.
      onConfirm={confirm}
      onCancel={cancel}
      carry={carry}
    />
  );
}

export function MapViewStatus({
  viewingPageId,
  onReturnToPlayers,
  returnAction = 'return',
  embedded = false,
  onOpenLevel,
}: MapViewStatusProps): JSX.Element | null {
  const { mp, pages, isGM } = useSessionContext();
  const { t } = useI18n();
  const { nameOf, flow } = useMapStripShow();
  const { attempt, ask } = flow;

  if (!mp.session) return null;
  // The player gets a plain line over the map (mockup table__player-maps): which map they see.
  // Not the GM's strip: no testid `map-view-status`, no actions.
  if (!isGM) {
    const name = nameOf(pages.activePageId) ?? '…';
    const text = t('mapView.playerSees').replace('{name}', name);
    return (
      <div className="flex-none border-b border-line bg-surface px-2 py-1 text-meta" data-testid="player-map-strip">
        <span className="flex min-w-0 items-center gap-1.5 text-ok" title={text}>
          <Play size={12} className="flex-none" aria-hidden="true" />
          <span className="truncate">{text}</span>
        </span>
      </div>
    );
  }

  // П9: the path to the map from the page store (not the join snapshot); one level is just the name.
  const pathOf = (pageId: string): Crumb[] => pagePath(pageId, pages.pages).map((p) => ({ id: p.id, label: p.name }));
  const prefix = (key: 'mapView.youSee' | 'mapView.together'): string => t(key).split('{name}')[0] ?? '';
  const path = (pageId: string, fallback: string, testid: string): JSX.Element => {
    const items = pathOf(pageId);
    return items.length <= 1 ? (
      <span className="truncate">{fallback}</span>
    ) : (
      <Crumbs
        items={items}
        onPick={onOpenLevel}
        hiddenLabel={t('sb.hidden')}
        testid={testid}
        itemTestid={`${testid}-level`}
        currentClassName="font-semibold"
      />
    );
  };

  const viewingName = nameOf(viewingPageId);
  const activeName = nameOf(pages.activePageId) ?? '…';
  const together = viewingPageId === pages.activePageId;
  const ownAttempt = attempt !== undefined && attempt.pageId === viewingPageId ? attempt : undefined;
  const youSee = t('mapView.youSee').replace('{name}', viewingName ?? '…');
  const playersSee = t('mapView.playersSee').replace('{name}', activeName);
  const togetherText = t('mapView.together').replace('{name}', activeName);

  return (
    <div
      className={`@container px-2 ${embedded ? 'min-w-0 flex-1' : 'flex-none border-b border-line bg-surface py-1'}`}
      data-testid="map-view-status"
      // Floating windows (the AI window) open below the strip, never over its buttons (useWindowManager).
      data-window-keep-clear=""
      data-together={together ? 'true' : 'false'}
      role="group"
      aria-label={t('mapView.label')}
    >
      {/* One line while the strip is wide enough for the names' minimum and both
          actions; narrower (< 32rem) it wraps instead of clipping the actions. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-meta" aria-live="polite">
        {together ? (
          <span
            className="flex min-w-0 items-center gap-1.5 text-ok"
            data-testid="map-view-together"
            title={`${togetherText}\n${t('mapView.togetherHint')}`}
          >
            <Play size={12} className="flex-none" aria-hidden="true" />
            <span className="flex-none whitespace-pre">{prefix('mapView.together')}</span>
            {path(pages.activePageId, activeName, 'map-view-path')}
          </span>
        ) : (
          <>
            {/* The names: «У тебя: …» / «У игроков: …» stay readable (GJ-31: they were cut to «У теб… У …») — when
                the strip is too narrow for them and the actions on one line, the actions go to the next one;
                the names truncate only when even a whole line is not enough. */}
            <span className="flex min-w-0 flex-[1_1_16rem] flex-wrap items-center gap-x-3 gap-y-0.5">
              <span
                className="flex min-w-0 items-center gap-1.5 font-medium text-hl"
                data-testid="map-view-you"
                title={`${youSee}\n${t('mapView.ownViewHint').replace('{name}', activeName)}`}
              >
                <Eye size={12} className="flex-none" aria-hidden="true" />
                <span className="flex-none whitespace-pre">{prefix('mapView.youSee')}</span>
                {path(viewingPageId, viewingName ?? '…', 'map-view-path')}
              </span>
              <span className="flex min-w-0 items-center gap-1.5 text-fg" data-testid="map-view-players" title={playersSee}>
                <Play size={12} className="flex-none" aria-hidden="true" />
                <span className="truncate">{playersSee}</span>
              </span>
              {/* T-17 (4): edits on a map the GM looks at alone are real — said on the strip, the full text is in the title. */}
              <span
                className="flex-none whitespace-nowrap text-meta text-dim @max-lg:hidden"
                data-testid="map-view-saved"
                title={t('mapView.ownViewHint').replace('{name}', activeName)}
              >
                {t('mapView.editsSaved')}
              </span>
            </span>
            <span className={`flex flex-none flex-wrap items-center gap-1 whitespace-nowrap @max-lg:flex-[0_1_auto] ${embedded ? 'ml-auto' : ''}`} data-testid="map-view-actions">
              <Button type="button" size="sm" variant="outline" data-testid="map-view-return" onClick={onReturnToPlayers}>
                {returnAction === 'open' ? t('mapView.openPlayersMap') : t('mapView.returnToPlayers')}
              </Button>
              {/* The one main action of the strip: it is the only one that changes what the players see. */}
              <Button
                type="button"
                size="sm"
                data-testid="map-view-show"
                disabled={attempt?.pending === true || viewingName === undefined}
                onClick={() => ask(viewingPageId)}
              >
                {ownAttempt?.pending === true ? t('mapView.showing') : t('mapView.show')}
              </Button>
            </span>
          </>
        )}
      </div>
      {/* A refusal / lost answer: its own line, in full (not squeezed by the names). */}
      {!together && ownAttempt !== undefined && !ownAttempt.pending ? (
        <p className="mt-0.5 break-words text-meta text-danger" data-testid="map-view-note">
          {ownAttempt.note}
        </p>
      ) : null}
      <StripShowConfirm flow={flow} nameOf={nameOf} />
    </div>
  );
}

export default MapViewStatus;
